import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Field, FormError } from "../components/Common";
import { Button } from "../components/ui/button";
import { fmtDate } from "../lib/labels";
import { ConfigTransfer } from "../components/ConfigTransfer";

const GROUPS = ["Guerra", "Conquista", "Pontos"];
const FIELDS = [
  { group: "Guerra", key: "cooldown_minutes", label: "Cooldown entre declarações (min)", min: 0, testid: "settings-cooldown-input", help: "Tempo que uma organização espera depois de declarar guerra para poder declarar outra." },
  { group: "Guerra", key: "overtime_max_minutes", label: "Prolongamento máximo (min)", min: 1, testid: "settings-overtime-input", help: "Sem empates: se no fim do combate os dois lados tiverem vivos na zona, há prolongamento até um lado sair. No limite ganha quem tiver mais vivos; em caso de igualdade, ganha o dono." },
  { group: "Conquista", key: "min_attackers_in_zone", label: "Mínimo de membros para iniciar", min: 1, testid: "settings-min-attackers-input", help: "Membros vivos necessários na zona, sem rivais vivos, para começar uma conquista. Depois de começar, basta 1." },
  { group: "Conquista", key: "capture_base_minutes", label: "Tempo base de conquista (min)", min: 1, testid: "settings-capture-base-input", help: "Tempo com 1 jogador. A velocidade multiplica pelo número de membros na zona (2 = metade, 3 = um terço…), sem limite." },
  { group: "Conquista", key: "standby_expire_minutes", label: "Expiração em espera (min)", min: 1, testid: "settings-standby-expire-input", help: "Uma conquista em espera (ninguém da organização na zona) durante este tempo expira e a barra volta a zero." },
  { group: "Pontos", key: "capture_points", label: "Pontos por conquista", min: 0, testid: "settings-capture-points-input", help: "Atribuídos quando a barra chega a 100% e a organização passa a dona da zona." },
  { group: "Pontos", key: "war_win_points", label: "Pontos por vitória em guerra", min: 0, testid: "settings-war-win-points-input", help: "Para quem vencer a guerra. Ganhe quem ganhar, a barra de conquista da zona volta a zero." },
  { group: "Pontos", key: "kill_points", label: "Pontos por kill", min: 0, testid: "settings-kill-points-input", help: "Por cada kill entre as duas organizações em guerra, durante o combate." },
  { group: "Pontos", key: "hold_points_per_hour", label: "Pontos de posse por hora", min: 0, testid: "settings-hold-points-input", help: "Atribuídos ao dono por cada hora completa de posse da zona." },
];


export default function SettingsPage() {
  const { data, reload } = useApi("/admin/settings");
  const { data: season } = useApi("/seasons/current", 60000);
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    for (const f of FIELDS) {
      const v = form[f.key];
      if (v === "" || v == null || !Number.isInteger(Number(v))) return setError(`${f.label}: deve ser um número inteiro.`);
      if (Number(v) < f.min) return setError(`${f.label}: deve ser igual ou superior a ${f.min}.`);
    }
    setBusy(true);
    try {
      const r = await api.put("/admin/settings", Object.fromEntries(FIELDS.map((f) => [f.key, Number(form[f.key])])));
      setForm(r.data);
      toast.success("Configurações guardadas.");
    } catch (err) {
      setError(errMsg(err, "Erro ao guardar configurações."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Regras globais" title="Configurações" />
      <form onSubmit={submit} className="panel fade-up max-w-3xl p-6" data-testid="settings-form" noValidate>
        {GROUPS.map((g) => (
          <fieldset key={g} className="mb-6">
            <legend className="mb-3 font-mono text-[11px] uppercase tracking-widest text-amber-300">{g}</legend>
            <div className="grid gap-5 md:grid-cols-2">
              {FIELDS.filter((f) => f.group === g).map((f) => (
                <div key={f.key}>
                  <Field label={f.label} type="number" min={String(f.min)} value={form[f.key] ?? ""} onChange={set(f.key)} testid={f.testid} />
                  <p className="mt-2 text-xs text-slate-500">{f.help}</p>
                </div>
              ))}
            </div>
          </fieldset>
        ))}
        <FormError message={error} testid="settings-error" />
        <Button type="submit" disabled={busy} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="settings-save-button">Guardar alterações</Button>
      </form>
      <div className="panel fade-up mt-6 max-w-xl p-6" data-testid="settings-next-reset">
        <div className="eyebrow">Temporada</div>
        <div className="mt-2 text-sm text-slate-300">
          Temporada atual: <b className="font-mono text-emerald-400">#{season?.number ?? "—"}</b>
        </div>
        <div className="mt-1 text-sm text-slate-300">
          Próximo reinício automático: <b className="font-mono" data-testid="settings-next-reset-at">{fmtDate(season?.next_reset_at)}</b> <span className="text-slate-500">(domingo 18:00, hora de Lisboa)</span>
        </div>
      </div>
      <ConfigTransfer onImported={reload} />
    </div>
  );
}
